import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Award, MapPin, MessageCircle, Phone, Wrench, ShieldCheck } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { LedTicker } from "../components/LedTicker";
import { SITE, IMG, KERALA_AREAS, OTHER_STATE_AREAS } from "../data/site";
import { PRODUCTS } from "../data/products";

const lineVariants = {
    hidden: { y: "110%" },
    show: (i) => ({
        y: "0%",
        transition: { duration: 0.9, delay: 0.25 + i * 0.16, ease: [0.22, 1, 0.36, 1] },
    }),
};

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yParallax = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const lines = [
        { text: "Scrolling LED Boards,", cls: "text-slate-50" },
        { text: "Video Walls &", cls: "led-glow-amber" },
        { text: "Smart Displays.", cls: "text-slate-50" },
    ];
    return (
        <section ref={ref} className="led-dots spotlight relative overflow-hidden" data-testid="home-hero">
            <motion.div style={{ y: yParallax }} className="pointer-events-none absolute -right-24 top-10 hidden h-[30rem] w-[30rem] rounded-full bg-[#FF9900]/10 blur-3xl lg:block" aria-hidden="true" />
            <div className="mx-auto max-w-7xl px-4 pb-16 pt-20 sm:px-6 lg:pb-24 lg:pt-28">
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.1, duration: 0.8 }}
                    className="font-led text-lg uppercase tracking-[0.35em] text-[#00A8FF]"
                >
                    Kochi · Kerala — Since 2009
                </motion.p>
                <h1 className="mt-6 font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-7xl">
                    {lines.map((l, i) => (
                        <span key={l.text} className="block overflow-hidden pb-1">
                            <motion.span
                                custom={i}
                                variants={lineVariants}
                                initial="hidden"
                                animate="show"
                                className={`block ${l.cls}`}
                            >
                                {l.text}
                            </motion.span>
                        </span>
                    ))}
                </h1>
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.85, duration: 0.7 }}
                    className="mt-6 max-w-2xl text-base text-slate-400 md:text-lg"
                >
                    Morningstar Enterprises designs, builds and installs scrolling LED display
                    boards, LED video walls, interactive flat panels and digital signage for
                    businesses and institutions across Kerala, Tamil Nadu and Karnataka.
                </motion.p>
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1, duration: 0.7 }}
                    className="mt-8 flex flex-wrap gap-4"
                >
                    <Link
                        to="/products/scrolling-led-boards"
                        data-testid="hero-explore-products"
                        className="group inline-flex items-center gap-2 rounded-full bg-[#FF9900] px-6 py-3 font-semibold text-black transition-transform hover:scale-105"
                    >
                        Explore Scrolling LED Boards
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                    <a
                        href={SITE.whatsapp}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="hero-whatsapp-cta"
                        className="inline-flex items-center gap-2 rounded-full border border-[#25D366]/60 px-6 py-3 font-semibold text-[#25D366] transition-colors hover:bg-[#25D366] hover:text-black"
                    >
                        <MessageCircle className="h-5 w-5" /> WhatsApp Us
                    </a>
                </motion.div>
            </div>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.15, duration: 0.8 }}
            >
                <LedTicker
                    messages={[
                        "Welcome to Morningstar Enterprises",
                        "Scrolling LED boards custom-built in Kochi",
                        "Malayalam · English · Hindi · Tamil · Kannada",
                        `Call ${SITE.phoneDisplay} for a free site consultation`,
                    ]}
                />
            </motion.div>
        </section>
    );
};

const Stats = () => (
    <section className="border-b border-slate-800/70" data-testid="home-stats">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px overflow-hidden lg:grid-cols-4">
            {[
                { k: "2009", v: "Established in Kochi" },
                { k: "3 States", v: "Kerala · Tamil Nadu · Karnataka" },
                { k: "1000s", v: "Of clients served" },
                { k: "NSIC", v: "& MSME ZED Certified" },
            ].map((s, i) => (
                <Reveal key={s.k} delay={i * 0.08} className="bg-[#0A0B10] p-8 text-center">
                    <p className="font-display text-3xl font-bold uppercase text-[#FF9900]">{s.k}</p>
                    <p className="mt-1 text-sm text-slate-400">{s.v}</p>
                </Reveal>
            ))}
        </div>
    </section>
);

const Memberships = () => (
    <section className="border-b border-slate-800/70 bg-[#0C0E16]" data-testid="home-memberships">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
            <Reveal>
                <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-center">
                    {[
                        "NSIC Certified",
                        "MSME ZED Certified",
                        "Member — SME Chamber of India",
                        "Member — The Cochin Chamber of Commerce & Industry",
                    ].map((m) => (
                        <span
                            key={m}
                            className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-400"
                        >
                            <Award className="h-4 w-4 text-[#FF9900]" aria-hidden="true" />
                            {m}
                        </span>
                    ))}
                </div>
            </Reveal>
        </div>
    </section>
);

const ProductBento = () => (
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6" data-testid="home-products">
        <Reveal>
            <p className="font-led text-lg uppercase tracking-[0.35em] text-[#00A8FF]">The Catalogue</p>
            <h2 className="mt-3 font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl lg:text-4xl">
                Nine product lines. One installation team.
            </h2>
            <p className="mt-3 max-w-2xl text-base text-slate-400">
                Every product page is a complete encyclopaedia — specifications, galleries,
                demo clips and FAQs — so you can decide before you even call us.
            </p>
        </Reveal>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PRODUCTS.map((p, i) => (
                <Reveal
                    key={p.slug}
                    delay={(i % 3) * 0.08}
                    className={p.flagship ? "sm:col-span-2 lg:row-span-2" : ""}
                >
                    <Link
                        to={`/products/${p.slug}`}
                        data-testid={`product-card-${p.slug}`}
                        className="group relative flex h-full min-h-56 flex-col justify-end overflow-hidden rounded-2xl border border-slate-800 bg-[#12141E]"
                    >
                        <img
                            src={p.heroImage}
                            alt={p.heroAlt}
                            loading="lazy"
                            className={`absolute inset-0 h-full w-full object-cover opacity-45 transition-all duration-700 group-hover:scale-105 group-hover:opacity-60 ${
                                p.flagship ? "" : ""
                            }`}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B10] via-[#0A0B10]/40 to-transparent" aria-hidden="true" />
                        <div className="relative p-6">
                            {p.flagship && (
                                <span className="mb-2 inline-block rounded-full bg-[#FF9900] px-3 py-1 text-xs font-bold uppercase tracking-wider text-black">
                                    Flagship
                                </span>
                            )}
                            <h3 className="font-display text-xl font-semibold uppercase tracking-wide text-slate-50 sm:text-2xl">
                                {p.name}
                            </h3>
                            <p className="mt-1 line-clamp-2 text-sm text-slate-300">{p.tagline}</p>
                            <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#FF9900]">
                                Open the encyclopaedia
                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                            </span>
                        </div>
                    </Link>
                </Reveal>
            ))}
        </div>
    </section>
);

const WhyUs = () => (
    <section className="border-y border-slate-800/70 bg-[#0C0E16]" data-testid="home-why-us">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
            <Reveal>
                <h2 className="font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl lg:text-4xl">
                    Why South India calls Morningstar
                </h2>
            </Reveal>
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {[
                    { icon: Award, t: "Certified & established", d: "NSIC and MSME ZED certified, member of SME Chamber of India and The Cochin Chamber of Commerce & Industry — serving since 2009." },
                    { icon: Wrench, t: "We build, not resell", d: "Scrolling LED boards are fabricated to your exact dimensions in Kochi — size, colour and language made to order." },
                    { icon: MapPin, t: "Three-state service", d: "Our own engineers install and support every product across Kerala, Tamil Nadu and Karnataka." },
                    { icon: ShieldCheck, t: "One partner, whole room", d: "Displays, sound, cameras, podiums and software integrated by one team with one phone number." },
                ].map((f, i) => (
                    <Reveal key={f.t} delay={i * 0.08}>
                        <article className="h-full rounded-2xl border border-slate-800 bg-[#12141E] p-6 transition-colors hover:border-[#FF9900]/40">
                            <f.icon className="h-7 w-7 text-[#FF9900]" aria-hidden="true" />
                            <h3 className="mt-4 font-display text-xl font-semibold uppercase tracking-wide">{f.t}</h3>
                            <p className="mt-2 text-sm leading-relaxed text-slate-400">{f.d}</p>
                        </article>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const AboutTeaser = () => (
    <section className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-20 sm:px-6 lg:grid-cols-2" data-testid="home-about-teaser">
        <Reveal>
            <img
                src={IMG.indoorLed}
                alt="Indoor LED screen display installed by Morningstar Enterprises"
                loading="lazy"
                className="w-full rounded-2xl border border-slate-800 object-cover"
            />
        </Reveal>
        <Reveal delay={0.1}>
            <p className="font-led text-lg uppercase tracking-[0.35em] text-[#00A8FF]">About Us</p>
            <h2 className="mt-3 font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl lg:text-4xl">
                A Kochi technology company, trusted since 2009
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-400">
                As a leading technology company, Morning Star Enterprises has served thousands
                of clients across Kerala, Karnataka and Tamil Nadu since 2009 — from single
                shopfront LED boards to complete smart-classroom and auditorium installations.
                We also consult on technology solutions for teaching, training, broadcasting,
                signage and conferencing.
            </p>
            <Link
                to="/about"
                data-testid="about-teaser-link"
                className="group mt-6 inline-flex items-center gap-2 font-semibold text-[#FF9900]"
            >
                Read our story
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
        </Reveal>
    </section>
);

const Verticals = () => (
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6" data-testid="home-verticals">
        <Reveal>
            <p className="font-led text-lg uppercase tracking-[0.35em] text-[#00A8FF]">Made for every space</p>
            <h2 className="mt-3 font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl lg:text-4xl">
                One supplier for every display your premises need
            </h2>
        </Reveal>
        <Reveal delay={0.1}>
            <div className="mt-8 flex flex-wrap gap-3">
                {[
                    "School digital notice board LED",
                    "Temple scrolling LED board",
                    "Church scrolling LED board",
                    "Mosque scrolling LED board",
                    "Hospital digital reception display",
                    "Retail store LED promotion board",
                    "Restaurant digital menu board LED",
                    "Corporate office lobby video wall",
                    "Outdoor advertising LED screen",
                    "LED display for defense training institutes",
                    "LED video wall for auditoriums",
                    "Control room video wall display",
                ].map((v) => (
                    <span
                        key={v}
                        className="rounded-full border border-slate-800 bg-[#12141E] px-4 py-2 text-sm text-slate-300 transition-colors hover:border-[#FF9900]/40"
                    >
                        {v}
                    </span>
                ))}
            </div>
        </Reveal>
    </section>
);

const ServiceAreas = () => (
    <section className="border-t border-slate-800/70 bg-[#0C0E16]" data-testid="home-service-areas">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
            <Reveal>
                <p className="font-led text-lg uppercase tracking-[0.35em] text-[#00A8FF]">Service Areas</p>
                <h2 className="mt-3 font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl lg:text-4xl">
                    From Palarivattom to every corner of Kerala
                </h2>
                <p className="mt-3 max-w-3xl text-base text-slate-400">
                    We supply, install and service scrolling LED boards, video walls, interactive
                    panels, sound systems and signage in every district of Kerala — and across
                    Tamil Nadu and Karnataka.
                </p>
            </Reveal>
            <Reveal delay={0.1}>
                <h3 className="mt-8 text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">
                    Kerala — all districts &amp; major towns
                </h3>
                <div className="mt-4 flex flex-wrap gap-2">
                    {KERALA_AREAS.map((a) => {
                        const citySlug = {
                            Kochi: "kochi",
                            Thrissur: "thrissur",
                            "Kozhikode (Calicut)": "kozhikode",
                            Thiruvananthapuram: "thiruvananthapuram",
                            Palakkad: "palakkad",
                            Kollam: "kollam",
                            Kottayam: "kottayam",
                            Kannur: "kannur",
                            Malappuram: "malappuram",
                            Alappuzha: "alappuzha",
                            Pathanamthitta: "pathanamthitta",
                            Wayanad: "wayanad",
                            Idukki: "idukki",
                            Kasaragod: "kasaragod",
                        }[a];
                        const cls =
                            "rounded-full border border-slate-800 bg-[#12141E] px-3 py-1.5 text-xs text-slate-400 transition-colors hover:border-[#FF9900]/40 hover:text-slate-200";
                        return citySlug ? (
                            <Link
                                key={a}
                                to={`/led-display-boards/${citySlug}`}
                                data-testid={`area-link-${citySlug}`}
                                className={`${cls} border-[#FF9900]/30 font-semibold text-[#FF9900]`}
                            >
                                {a}
                            </Link>
                        ) : (
                            <span key={a} className={cls}>
                                {a}
                            </span>
                        );
                    })}
                </div>
                <h3 className="mt-8 text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">
                    Tamil Nadu &amp; Karnataka
                </h3>
                <div className="mt-4 flex flex-wrap gap-2">
                    {OTHER_STATE_AREAS.map((a) => (
                        <span
                            key={a}
                            className="rounded-full border border-slate-800 bg-[#12141E] px-3 py-1.5 text-xs text-slate-400 transition-colors hover:border-[#00A8FF]/40 hover:text-slate-200"
                        >
                            {a}
                        </span>
                    ))}
                </div>
            </Reveal>
        </div>
    </section>
);

const CtaBand = () => (
    <section className="led-dots border-t border-slate-800/70" data-testid="home-cta-band">
        <div className="mx-auto max-w-7xl px-4 py-16 text-center sm:px-6">
            <Reveal>
                <h2 className="font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl lg:text-4xl">
                    Tell us the wall. <span className="led-glow-amber">We'll light it up.</span>
                </h2>
                <p className="mx-auto mt-3 max-w-xl text-base text-slate-400">
                    Send a photo and rough dimensions of your site on WhatsApp — our team replies
                    with the right product, pitch and budget.
                </p>
                <div className="mt-8 flex flex-wrap justify-center gap-4">
                    <a
                        href={SITE.whatsapp}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="cta-band-whatsapp"
                        className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 font-semibold text-black shadow-[0_0_18px_rgba(37,211,102,0.45)] transition-transform hover:scale-105"
                    >
                        <MessageCircle className="h-5 w-5" /> {SITE.phoneDisplay}
                    </a>
                    <a
                        href={SITE.phoneTel}
                        data-testid="cta-band-call"
                        className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-6 py-3 font-semibold text-slate-200 transition-colors hover:border-[#FF9900] hover:text-[#FF9900]"
                    >
                        <Phone className="h-5 w-5" /> Call Now
                    </a>
                </div>
            </Reveal>
        </div>
    </section>
);

export default function Home() {
    return (
        <>
            <Seo
                title="LED Display Boards Manufacturer in Kochi | Morningstar Enterprises"
                description="Morningstar Enterprises, Kochi — scrolling LED boards, LED video walls, interactive flat panels & digital signage. Serving Kerala, Tamil Nadu & Karnataka since 2009."
                path="/"
                keywords="scrolling LED board Kerala, LED name board Kochi, LED video wall Kerala, scrolling LED message board, programmable LED sign board, interactive flat panel Kerala, digital signage Kochi, custom digital signage solutions Kerala, outdoor advertising LED screen, LED display Thrissur, LED board Kozhikode, LED sign board Thiruvananthapuram, LED board Kollam, LED display Kottayam, LED board Palakkad, LED display Kannur, LED board Guruvayur, LED display Munnar"
                jsonLd={[
                    {
                        "@context": "https://schema.org",
                        "@type": "WebSite",
                        name: "Morningstar Enterprises",
                        url: SITE.domain + "/",
                    },
                ]}
            />
            <Hero />
            <Stats />
            <Memberships />
            <ProductBento />
            <LedTicker
                accent="blue"
                messages={[
                    "LED Video Walls · P1.5 to P10",
                    "Wisdomatic Interactive Flat Panels",
                    "Audio Podiums & Digital Lecterns",
                    "Video Conferencing & Online Class Solutions",
                    "Digital Signage across South India",
                ]}
            />
            <WhyUs />
            <AboutTeaser />
            <Verticals />
            <ServiceAreas />
            <CtaBand />
        </>
    );
}
